import type { ProposalItemUnit, ProposalNameResolver } from "~/helpers/aiChat/summarizeProposal";

import itemShippingUnit, { type ShippingUomItem } from "~fulfill/inventory/stock/helpers/itemShippingUnit";
import rowShippingUnit, { type ShippingUomRow } from "~fulfill/inventory/stock/helpers/rowShippingUnit";
import { type ConfigCategoryIdentity } from "~fulfill/product/helpers/configCategoryDisplay";

interface CacheNode extends ShippingUomItem, ShippingUomRow {
	code?: unknown;
	configCategoryId?: unknown;
	customerId?: unknown;
	id?: unknown;
	itemId?: unknown;
	itemName?: unknown;
	name?: unknown;
}

interface QueryEntry {
	data?: unknown;
	endpointName?: string;
}

const CUSTOMER_ENDPOINTS = new Set(["GetCustomersPaginated", "GetCustomers"]);
const ITEM_ENDPOINTS = new Set(["GetItemsPaginated", "GetItems"]);
const INVENTORY_ENDPOINTS = new Set(["GetInventoryItemsPaginated"]);
const CONFIG_CATEGORY_ENDPOINTS = new Set(["GetConfigCategoriesPaginated", "GetConfigCategories"]);

const asString = (value: unknown): string | undefined =>
	typeof value === "string" && value.trim() ? value.trim() : undefined;

const readNodes = (data: unknown, collection: string): CacheNode[] => {
	if (!data || typeof data !== "object") return [];
	const wrapper = (data as Record<string, unknown>)[collection];
	if (!wrapper || typeof wrapper !== "object") return [];
	const nodes = (wrapper as Record<string, unknown>).nodes;
	return Array.isArray(nodes) ? (nodes as CacheNode[]) : [];
};

const indexFirst = <T>(map: Map<string, T>, ids: (string | undefined)[], value: null | T | undefined) => {
	if (!value) return;
	for (const id of ids) {
		if (id && !map.has(id)) map.set(id, value);
	}
};

const readConfigCategory = (node: CacheNode): ConfigCategoryIdentity | undefined => {
	const configCategoryId = asString(node.configCategoryId);
	if (!configCategoryId) return undefined;
	return { code: asString(node.code) ?? "", configCategoryId, name: asString(node.name) ?? "" };
};

export const buildProposalNameResolver = (apiSliceState: unknown): ProposalNameResolver => {
	const customers = new Map<string, string>();
	const items = new Map<string, string>();
	const units = new Map<string, ProposalItemUnit>();
	const itemRows: CacheNode[] = [];
	const configCategories: ConfigCategoryIdentity[] = [];
	const state = apiSliceState as { queries?: Record<string, QueryEntry | undefined> } | undefined;
	const queries = state?.queries ?? {};
	for (const entry of Object.values(queries)) {
		const endpoint = entry?.endpointName;
		if (!endpoint) continue;
		if (CUSTOMER_ENDPOINTS.has(endpoint)) {
			for (const node of readNodes(entry?.data, "customers")) {
				indexFirst(customers, [asString(node.id), asString(node.customerId)], asString(node.name));
			}
		} else if (ITEM_ENDPOINTS.has(endpoint)) {
			for (const node of readNodes(entry?.data, "items")) {
				indexFirst(items, [asString(node.id), asString(node.itemId)], asString(node.name));
				itemRows.push(node);
			}
		} else if (INVENTORY_ENDPOINTS.has(endpoint)) {
			for (const node of readNodes(entry?.data, "inventoryItems")) {
				indexFirst(items, [asString(node.itemId)], asString(node.itemName));
				indexFirst(units, [asString(node.itemId)], rowShippingUnit(node));
			}
		} else if (CONFIG_CATEGORY_ENDPOINTS.has(endpoint)) {
			for (const node of readNodes(entry?.data, "configCategories")) {
				const category = readConfigCategory(node);
				if (category) configCategories.push(category);
			}
		}
	}
	for (const node of itemRows) {
		indexFirst(units, [asString(node.id), asString(node.itemId)], itemShippingUnit(node, configCategories));
	}
	return {
		customer: (id) => customers.get(id),
		item: (id) => items.get(id),
		itemUnit: (id) => units.get(id)
	};
};

export default buildProposalNameResolver;
